<template>
  <a-modal
    :visible="visible"
    :width="1320"
    :footer="null"
    destroy-on-close
    @cancel="handleCancel"
  >
    <template #title>
      <span class="modal-title-text">新增付费面板</span>
    </template>

    <a-row :gutter="28" class="modal-body-row">
      <a-col :span="15" class="form-col">
        <div class="basic-info-card">
          <div class="poster-wrap">
            <img
              v-if="coverUrl"
              class="poster-img"
              :src="coverUrl"
              alt=""
            />
            <div v-else class="poster-placeholder">无封面</div>
            <span class="poster-badge">付费</span>
          </div>
          <div class="basic-meta">
            <div class="basic-meta-title">基础信息</div>
            <div class="basic-meta-line">总集数 {{ dramaMeta.totalEp }}</div>
            <div class="basic-meta-line">单集价格 {{ dramaMeta.unitPrice }}</div>
          </div>
        </div>

        <a-form
          ref="formRef"
          layout="vertical"
          class="paid-panel-form"
          :model="form"
          :rules="formRules"
        >
          <a-form-item label="面板名称" name="panel_name">
            <a-input
              v-model:value="form.panel_name"
              placeholder="请输入面板名称"
              allow-clear
            />
          </a-form-item>

          <a-form-item>
            <template #label>
              <span><span class="req">*</span>付费集数</span>
            </template>
            <div class="paid-range-row">
              <a-input-number
                v-model:value="form.paid_from"
                :min="1"
                :max="form.paid_to"
                :precision="0"
                class="range-input"
              />
              <span class="range-sep">-</span>
              <a-input-number
                :value="dramaMeta.totalEp"
                :precision="0"
                class="range-input"
                disabled
              />
            </div>
          </a-form-item>

          <div class="tier-block">
            <div class="tier-head">
              <span class="th th-tier">档位</span>
              <span class="th th-unlock">
                <span class="req">*</span>
                解锁集数
                <a-tooltip :title="TOOLTIP_UNLOCK_EPISODES">
                  <InfoCircleOutlined class="th-info-icon" />
                </a-tooltip>
              </span>
              <span class="th th-discount">
                <span class="req">*</span>
                解锁折扣
                <a-tooltip :title="TOOLTIP_UNLOCK_DISCOUNT">
                  <InfoCircleOutlined class="th-info-icon" />
                </a-tooltip>
              </span>
              <span class="th th-pay">
                用户需支付金额
              </span>
              <span class="th th-unit">
                优惠后单集价格
              </span>
              <span class="th th-op">操作</span>
            </div>
            <div v-for="(row, idx) in form.tiers" :key="row.uid" class="tier-row">
              <span class="td td-tier">档位{{ tierCn(idx) }}</span>
              <span class="td td-unlock">
                <a-input-number
                  v-model:value="row.unlock_episodes"
                  :min="1"
                  :max="paidEpisodeCapacity"
                  :precision="0"
                  placeholder="必填，请输入整数"
                  class="tier-input"
                />
              </span>
              <span class="td td-discount">
                <a-input-number
                  v-model:value="row.unlock_discount"
                  :min="0.01"
                  :max="1"
                  :step="0.01"
                  :precision="2"
                  class="tier-input"
                  @blur="() => onDiscountBlur(row)"
                />
              </span>
              <span class="td td-pay">{{ cellUserPay(row) }}</span>
              <span class="td td-unit">{{ cellUnitPriceAfter(row) }}</span>
              <span class="td td-op">
                <template v-if="form.tiers.length <= MIN_TIERS">
                  <a-tooltip :title="TOOLTIP_MIN_TIERS">
                    <span class="del-wrap">
                      <a class="link-disabled" @click.prevent>删除</a>
                    </span>
                  </a-tooltip>
                </template>
                <template v-else>
                  <a class="link-del" @click.prevent="removeTier(idx)">删除</a>
                </template>
              </span>
            </div>
            <div v-if="form.tiers.length < MAX_TIERS" class="tier-add-row">
              <a-tooltip :title="TOOLTIP_MAX_TIERS">
                <span class="add-wrap">
                  <a-button type="dashed" block @click="addTier">+ 添加</a-button>
                </span>
              </a-tooltip>
            </div>
          </div>
        </a-form>
      </a-col>

      <a-col :span="9" class="preview-col">
        <DyNativePhonePreview
          v-model:selected-tier-index="previewSelectedTier"
          :config="phonePreviewConfig"
        />
      </a-col>
    </a-row>

    <div class="modal-footer-bar">
      <a-button @click="handleCancel">取消</a-button>
      <a-button type="primary" :loading="confirmLoading" @click="handleConfirm">
        确认并复制短剧专辑链接
      </a-button>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
import DyNativePhonePreview from './DyNativePhonePreview.vue'
import Pub from '@/utils/public'

const TOOLTIP_UNLOCK_EPISODES = '解锁集数需小于等于该短剧的付费总集数。'
const TOOLTIP_UNLOCK_DISCOUNT =
  '解锁折扣为乘数（如 1 无折扣、0.99 即 99 折）。优惠后单集价 = 原单集价 × 解锁折扣；用户支付 = 原单集价 × 解锁折扣 × 集数。'
const TOOLTIP_MIN_TIERS = '最少需设置2个档位'
const TOOLTIP_MAX_TIERS = '最多设置4个档位'

const MIN_TIERS = 2
const MAX_TIERS = 4

const emit = defineEmits(['success'])

const visible = ref(false)
const confirmLoading = ref(false)
const dramaRecord = ref(null)
const previewSelectedTier = ref(0)
const formRef = ref(null)

const form = reactive({
  panel_name: '',
  paid_from: 1,
  paid_to: 70,
  tiers: []
})

const formRules = {
  panel_name: [{ required: true, message: '请输入面板名称' }]
}

const dramaMeta = computed(() => {
  const r = dramaRecord.value || {}
  const total = r.total_ep != null ? Number(r.total_ep) : 70
  const raw = r.unit_episode_price != null ? Number(r.unit_episode_price) : null
  const up = Number.isFinite(raw) ? (raw >= 10 ? raw / 100 : raw) : 1.5
  return {
    totalEp: total,
    unitPrice: Number.isFinite(up) ? up.toFixed(2) : '1.50',
    unitPriceNum: Number.isFinite(up) ? up : 1.5
  }
})

const coverUrl = computed(() => {
  const r = dramaRecord.value
  const u = r?.cover?.[0]?.url
  return u ? Pub.CDN_URL(u) : ''
})

const paidEpisodeCapacity = computed(() => {
  const a = Number(form.paid_from)
  const b = dramaMeta.value.totalEp
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return dramaMeta.value.totalEp
  return b - a + 1
})

const phonePreviewConfig = computed(() => ({
  tiers: form.tiers.map(t => {
    const u = Number(t.unlock_episodes)
    const p0 = dramaMeta.value.unitPriceNum
    const d = Number(t.unlock_discount)
    const ok = Number.isFinite(u) && u >= 1 && Number.isFinite(d) && d > 0 && d <= 1
    const origTotal = Number.isFinite(u) && u >= 1 && Number.isFinite(p0) ? +(u * p0).toFixed(2) : null
    const payTotal = ok ? +(u * p0 * d).toFixed(2) : null
    const save =
      origTotal != null && payTotal != null ? +(origTotal - payTotal).toFixed(2) : null
    return {
      key: t.uid,
      unlockEpisodes: ok ? u : null,
      payDiscountedTotal: payTotal,
      saveYuan: save
    }
  })
}))

watch(
  () => form.tiers.length,
  () => {
    if (previewSelectedTier.value >= form.tiers.length) {
      previewSelectedTier.value = Math.max(0, form.tiers.length - 1)
    }
  }
)

function tierCn(i) {
  return ['一', '二', '三', '四'][i] || String(i + 1)
}

function createTier() {
  return {
    uid: `tier-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    unlock_episodes: null,
    unlock_discount: 1
  }
}

function calcUnitPriceNum() {
  return dramaMeta.value.unitPriceNum
}

/** 用户需支付金额：原单集价格 × 解锁折扣 × 集数 */
function calcUserPayTotal(row) {
  const u = Number(row.unlock_episodes)
  const p0 = calcUnitPriceNum()
  const d = Number(row.unlock_discount)
  if (!Number.isFinite(u) || u < 1 || !Number.isFinite(p0) || !Number.isFinite(d) || d <= 0) return null
  return +(u * p0 * d).toFixed(2)
}

function cellUserPay(row) {
  const v = calcUserPayTotal(row)
  return v == null ? '- 元' : `${v.toFixed(2)}元`
}

/** 优惠后单集价格：原单集价格 × 解锁折扣 */
function calcDiscountedUnitPrice(row) {
  const p0 = calcUnitPriceNum()
  const d = Number(row.unlock_discount)
  if (!Number.isFinite(p0) || !Number.isFinite(d) || d <= 0) return null
  return +(p0 * d).toFixed(2)
}

function cellUnitPriceAfter(row) {
  const v = calcDiscountedUnitPrice(row)
  return v == null ? '- 元' : `${v.toFixed(2)}元`
}

function onDiscountBlur(row) {
  let v = Number(row.unlock_discount)
  if (!Number.isFinite(v)) {
    row.unlock_discount = 1
    return
  }
  if (v > 1) {
    row.unlock_discount = 1
    return
  }
  if (v <= 0) {
    row.unlock_discount = 0.01
    return
  }
  row.unlock_discount = +v.toFixed(2)
}

function resetForm() {
  const r = dramaRecord.value || {}
  const total = r.total_ep != null ? Number(r.total_ep) : 70
  const start = r.start_pay_eq != null ? Number(r.start_pay_eq) : 1
  form.panel_name = ''
  form.paid_from = Number.isFinite(start) && start >= 1 ? start : 1
  form.paid_to = Number.isFinite(total) ? total : 70
  if (form.paid_from > form.paid_to) {
    form.paid_from = 1
    form.paid_to = total
  }
  form.tiers = [createTier(), createTier()]
  previewSelectedTier.value = 0
}

/**
 * @param {object} record 片库行 drama_list
 * @param {object} [preset] 回显：tiers 支持 unlock_discount / discount；若仅有 unit_price_after（旧版存的优惠后单价）则按 ÷原单集价 换算为折扣
 */
function showModal(record, preset) {
  dramaRecord.value = record || null
  visible.value = true
  resetForm()
  if (preset && typeof preset === 'object') {
    if (preset.panel_name != null) form.panel_name = preset.panel_name
    if (preset.paid_from != null) form.paid_from = preset.paid_from
    if (preset.paid_to != null) form.paid_to = preset.paid_to
    if (Array.isArray(preset.tiers) && preset.tiers.length) {
      const p0 = dramaMeta.value.unitPriceNum
      form.tiers = preset.tiers.map(t => {
        let d = t.unlock_discount != null ? Number(t.unlock_discount) : null
        if (!Number.isFinite(d) && t.discount != null) d = Number(t.discount)
        if (!Number.isFinite(d) && t.unit_price_after != null && Number.isFinite(p0) && p0 > 0) {
          d = Number(t.unit_price_after) / p0
        }
        if (!Number.isFinite(d) || d <= 0) d = 1
        d = Math.min(1, Math.max(0.01, d))
        return {
          uid: t.uid || `tier-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          unlock_episodes: t.unlock_episodes,
          unlock_discount: +d.toFixed(2)
        }
      })
      while (form.tiers.length < MIN_TIERS) {
        form.tiers.push(createTier())
      }
      while (form.tiers.length > MAX_TIERS) {
        form.tiers.pop()
      }
    }
  }
}

function handleCancel() {
  visible.value = false
}

function validateTiers() {
  const cap = paidEpisodeCapacity.value
  for (let i = 0; i < form.tiers.length; i++) {
    const row = form.tiers[i]
    const u = Number(row.unlock_episodes)
    if (!Number.isFinite(u) || u < 1) {
      message.error(`档位${tierCn(i)}：请填写有效的解锁集数`)
      return false
    }
    if (u > cap) {
      message.error(`档位${tierCn(i)}：解锁集数不能大于付费总集数（${cap}）`)
      return false
    }
    const d = Number(row.unlock_discount)
    if (!Number.isFinite(d) || d <= 0 || d > 1) {
      message.error(`档位${tierCn(i)}：解锁折扣须为 0.01～1 的乘数（如 1 无折、0.99 九九折）`)
      return false
    }
  }
  return true
}

function handleConfirm() {
  formRef.value
    ?.validate()
    .then(() => {
      if (form.paid_from == null || form.paid_to == null) {
        message.error('请填写付费集数区间')
        return
      }
      if (form.paid_from > form.paid_to) {
        message.error('付费集数起始不能大于结束集')
        return
      }
      if (!validateTiers()) return
      confirmLoading.value = true
      const mockAlbumUrl = `https://example.com/album/${dramaRecord.value?.id || 'mock'}`
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(mockAlbumUrl)
          .then(() => {
            message.success('已复制短剧专辑链接（示例）')
            emit('success', buildPayload())
            visible.value = false
          })
          .catch(() => message.error('复制失败'))
          .finally(() => {
            confirmLoading.value = false
          })
      } else {
        message.info('当前环境不支持复制，请手动保存链接')
        confirmLoading.value = false
      }
    })
    .catch(() => {})
}

function buildPayload() {
  const p0 = calcUnitPriceNum()
  return {
    drama_id: dramaRecord.value?.id,
    panel_name: form.panel_name,
    paid_from: form.paid_from,
    paid_to: dramaMeta.value.totalEp,
    tiers: form.tiers.map(t => {
      const d = Number(t.unlock_discount)
      const discUnit =
        Number.isFinite(p0) && Number.isFinite(d) && d > 0 ? +(p0 * d).toFixed(2) : null
      return {
        unlock_episodes: t.unlock_episodes,
        unlock_discount: t.unlock_discount,
        unit_price_after: discUnit
      }
    })
  }
}

function addTier() {
  if (form.tiers.length >= MAX_TIERS) return
  form.tiers.push(createTier())
}

function removeTier(idx) {
  if (form.tiers.length <= MIN_TIERS) return
  form.tiers.splice(idx, 1)
}

defineExpose({ showModal })
</script>

<style scoped>
.modal-title-text {
  margin-right: 8px;
}
.modal-title-icon {
  color: rgba(0, 0, 0, 0.45);
  cursor: help;
}
.modal-body-row {
  margin-top: 8px;
}
.basic-info-card {
  display: flex;
  gap: 16px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #f0f0f0;
}
.poster-wrap {
  position: relative;
  flex-shrink: 0;
  width: 56px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f5f5;
}
.poster-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.poster-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.35);
}
.poster-badge {
  position: absolute;
  left: 0;
  top: 0;
  font-size: 10px;
  background: #f5a623;
  color: #1a1a1a;
  padding: 1px 4px;
  border-radius: 0 0 4px 0;
}
.basic-meta-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.88);
}
.basic-meta-line {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.6;
}
.req {
  color: #ff4d4f;
  margin-right: 4px;
}
.label-info-icon,
.th-info-icon {
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.45);
  cursor: help;
}
.paid-range-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.range-input {
  width: 120px;
}
.range-sep {
  color: rgba(0, 0, 0, 0.45);
}
.tier-block {
  margin-top: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}
.tier-head {
  display: grid;
  grid-template-columns: 80px minmax(168px, 1fr) 140px 150px 130px 72px;
  gap: 12px;
  padding: 12px 16px;
  background: #fafafa;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  align-items: center;
}
.th .req {
  color: #ff4d4f;
  margin-right: 2px;
}
.tier-row {
  display: grid;
  grid-template-columns: 80px minmax(168px, 1fr) 140px 150px 130px 72px;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.88);
}
.td-pay,
.td-unit {
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
}
.tier-input {
  width: 100%;
}
.link-del {
  color: #1890ff;
  cursor: pointer;
}
.link-disabled {
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}
.del-wrap,
.add-wrap {
  display: block;
}
.tier-add-row {
  padding: 12px;
  border-top: 1px solid #f0f0f0;
}
.preview-col {
  border-left: 1px solid #f0f0f0;
  padding-left: 16px;
  min-height: 480px;
}
.modal-footer-bar {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  text-align: right;
}
.modal-footer-bar .ant-btn + .ant-btn {
  margin-left: 12px;
}
</style>
